import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/shared/api';
import { getActiveCompanyId, getActiveCompanyName, setActiveCompany } from '@/shared/activeCompany';
import './SubaccountBar.less';

interface SessionCompany {
  id: string;
  name: string;
  kind: 'partner' | 'subaccount' | 'standard';
  role?: string | null;
  canEnter: boolean;
  parentName?: string | null;
}

export const SubaccountBar: FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<SessionCompany[]>([]);
  const [activeId, setActiveId] = useState(getActiveCompanyId());
  const [activeName, setActiveName] = useState(getActiveCompanyName());

  useEffect(() => {
    let cancelled = false;
    api.get('api/session/companies').json<{ companies: SessionCompany[] }>()
      .then((data) => {
        if (cancelled) return;
        const rows = data.companies || [];
        setCompanies(rows);
        const show = rows.some((row) => row.kind === 'partner' || row.kind === 'subaccount') || rows.length > 1;
        if (show) sessionStorage.setItem('showPartnerNav', '1');
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const enterable = companies.filter((company) => company.canEnter);
  const show = companies.some((company) => company.kind === 'partner' || company.kind === 'subaccount') || enterable.length > 1;
  if (!show) return null;

  const active = companies.find((company) => company.id === activeId);
  const label = active
    ? [active.parentName, active.name].filter(Boolean).join(' / ')
    : activeName || 'Choose account';

  const onChange = async (companyId: string) => {
    if (!companyId || companyId === activeId) return;
    const company = companies.find((row) => row.id === companyId);
    if (!window.confirm(`Switch to ${company?.name || 'this account'}? Unsaved work on this page will be left behind.`)) {
      return;
    }
    await api.post('api/session/active-company', { json: { companyId } }).json();
    setActiveCompany(companyId, company?.name);
    setActiveId(companyId);
    setActiveName(company?.name || '');
    window.location.assign('/');
  };

  const exitHome = async () => {
    await api.post('api/session/active-company', { json: { companyId: null } });
    setActiveCompany(null);
    setActiveId('');
    setActiveName('');
    navigate('/partner');
  };

  return (
    <div className="subaccount-context-bar">
      <span className="subaccount-context-label">{label}</span>
      <select
        aria-label="Switch account"
        value={activeId}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Switch account</option>
        {enterable.map((company) => (
          <option key={company.id} value={company.id}>
            {company.parentName ? `${company.parentName} / ${company.name}` : company.name}
          </option>
        ))}
      </select>
      <button type="button" onClick={() => navigate('/partner')}>Customers</button>
      {active?.kind === 'subaccount' && (
        <button type="button" onClick={exitHome}>Exit</button>
      )}
    </div>
  );
};
